import type { ComponentProps, ReactNode } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Card, CardContent } from "@/components/ui/card";

/** Tokens visuais alinhados ao fluxo público (Inscricoes) e shadcn do SysIgreja */
export const siteUi = {
  /** Fundo real do site público — extraído de `page` pra poder ser reaproveitado dentro do
   * "canvas" do construtor visual (o cartão que emula fielmente a página publicada) sem
   * herdar o `min-h-screen`, que faria sentido só na página inteira. */
  pageBg: "bg-linear-to-br from-white via-blue-50/50 to-purple-50/50 text-gray-800",
  page: "relative min-h-screen bg-linear-to-br from-white via-blue-50/50 to-purple-50/50 text-gray-800",
  container: "container mx-auto max-w-7xl px-4 md:px-6",
  glass:
    "rounded-2xl border border-white/30 bg-white/80 shadow-lg backdrop-blur-lg",
  glassSoft:
    "rounded-2xl border border-white/40 bg-white/60 shadow-md backdrop-blur-sm",
  nav: "rounded-full border border-white/30 bg-white/80 shadow-lg backdrop-blur-lg",
  headingXl:
    "text-3xl font-extrabold tracking-tight text-gray-900 sm:text-4xl md:text-5xl",
  headingLg: "text-2xl font-bold text-gray-900 sm:text-3xl",
  headingMd: "text-xl font-bold text-gray-900",
  subtitle: "mt-2 text-base text-gray-600",
  label: "text-xs font-semibold uppercase tracking-wider text-purple-600",
  link: "text-sm font-semibold text-[var(--site-brand,#2563eb)] transition-colors hover:text-[var(--site-brand-dark,#1d4ed8)]",
  muted: "text-sm text-gray-600",
  mutedXs: "text-xs text-gray-500",
  gradientText: "text-gray-900",
  // Sem cor de marca customizada, os fallbacks reproduzem exatamente o azul/roxo padrão
  // de sempre — só muda de fato quando o admin escolhe uma cor em "Cor da marca".
  gradientBg: "bg-linear-to-r from-[var(--site-brand,#2563eb)] to-[var(--site-brand-dark,#9333ea)]",
  gradientHero:
    "bg-linear-to-br from-[var(--site-brand,#2563eb)] via-[var(--site-brand,#1d4ed8)] to-[var(--site-brand-dark,#9333ea)]",
  sectionAlt: "bg-white/40 py-16 backdrop-blur-sm border-y border-white/50",
  input:
    "rounded-xl border-2 border-gray-200/80 bg-white/90 shadow-inner focus:border-blue-400 focus:ring-2 focus:ring-blue-300/50",
  iconPrimary: "bg-blue-100 text-blue-600",
  iconSecondary: "bg-purple-100 text-purple-600",
  iconTertiary: "bg-violet-100 text-violet-600",
  /** Botão padrão dos banners/CTAs (fundo branco sobre gradiente) */
  ctaButton:
    "border-0 bg-white font-bold text-blue-700 shadow-xl hover:bg-white/95 hover:text-blue-700",
} as const;

import type { SiteSectionTone } from "@/interfaces/site-section.interface";

/**
 * Fundo por seção no construtor visual — mesma paleta de `gradientHero`/`gradientBg`, e as
 * mesmas cores do protótipo (BG.night/royal/dawn). Sem padding aqui de propósito: o
 * conteúdo de cada seção já traz seu próprio respiro (SitePageContainer + py-*); isto só
 * pinta o "cartão" por trás dele, do mesmo jeito que o protótipo aplica `TONE[tone]` como
 * fundo atrás de um conteúdo que já tem padding embutido.
 */
export const TONE_CLASSNAMES: Record<SiteSectionTone, string> = {
  plain: "",
  soft: "rounded-[2rem] bg-[#eef2fb]",
  night: "rounded-[2rem] text-white bg-linear-to-br from-slate-800 via-slate-900 to-zinc-950",
  royal:
    "rounded-[2rem] text-white bg-linear-to-r from-[var(--site-brand,#2563eb)] to-[var(--site-brand-dark,#9333ea)]",
  dawn: "rounded-[2rem] text-white bg-linear-to-br from-slate-700 via-purple-800 to-purple-900",
};

export const TONE_LABELS: Record<SiteSectionTone, string> = {
  plain: "Fundo claro",
  soft: "Cinza azulado",
  night: "Noite",
  royal: "Gradiente da marca",
  dawn: "Roxo profundo",
};

export function SiteCtaButton({
  className,
  variant = "outline",
  ...props
}: ComponentProps<typeof Button>) {
  return (
    <Button
      variant={variant}
      className={cn(
        siteUi.ctaButton,
        "bg-none from-transparent to-transparent hover:border-transparent hover:text-blue-700",
        className,
      )}
      {...props}
    />
  );
}

export function SiteDecorativeBackground() {
  return (
    <div className="pointer-events-none absolute inset-0 z-0 overflow-hidden opacity-20">
      <div className="absolute -top-32 -right-32 h-96 w-96 rounded-full bg-blue-200 blur-3xl" />
      <div className="absolute -bottom-32 -left-32 h-96 w-96 rounded-full bg-purple-200 blur-3xl" />
    </div>
  );
}

export function SitePageContainer({
  children,
  className,
  id,
}: {
  children: ReactNode;
  className?: string;
  id?: string;
}) {
  return (
    <div id={id} className={cn(siteUi.container, className)}>
      {children}
    </div>
  );
}

export function SitePageHeader({
  title,
  description,
}: {
  title: string;
  description?: string;
}) {
  return (
    <header className="mb-8 md:mb-10">
      <h1 className={siteUi.headingXl}>
        <span className={siteUi.gradientText}>{title}</span>
      </h1>
      {description && <p className={siteUi.subtitle}>{description}</p>}
    </header>
  );
}

export function SiteSectionHeading({
  title,
  description,
  className,
}: {
  title: string;
  description?: string;
  className?: string;
}) {
  return (
    <div className={cn("mb-8 md:mb-10", className)}>
      <h2 className={siteUi.headingLg}>
        <span className={siteUi.gradientText}>{title}</span>
      </h2>
      {description && <p className={siteUi.subtitle}>{description}</p>}
    </div>
  );
}

export function SiteGlassCard({
  children,
  className,
  hover = false,
}: {
  children: ReactNode;
  className?: string;
  hover?: boolean;
}) {
  return (
    <div
      className={cn(
        siteUi.glass,
        hover && "transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl",
        className,
      )}
    >
      {children}
    </div>
  );
}

export function SiteQuickAccessCard({
  to,
  onClick,
  icon: Icon,
  imageUrl,
  title,
  description,
  tone = "primary",
}: {
  to?: string;
  onClick?: () => void;
  icon: React.ComponentType<{ className?: string }>;
  imageUrl?: string | null;
  title: string;
  description: string;
  tone?: "primary" | "secondary" | "tertiary";
}) {
  const toneClass = {
    primary: siteUi.iconPrimary,
    secondary: siteUi.iconSecondary,
    tertiary: siteUi.iconTertiary,
  }[tone];

  const className = cn(
    siteUi.glass,
    "flex flex-col items-center p-6 text-center transition-all duration-300 hover:-translate-y-1 hover:shadow-xl",
    onClick && "cursor-pointer",
  );

  const content = (
    <>
      <div
        className={cn(
          "mb-4 flex h-16 w-16 items-center justify-center rounded-2xl",
          imageUrl ? "bg-white ring-1 ring-gray-200" : toneClass,
        )}
      >
        {imageUrl ? (
          <img src={imageUrl} alt="" className="h-10 w-10 object-contain" loading="lazy" />
        ) : (
          <Icon className="h-8 w-8" />
        )}
      </div>
      <h3 className="font-bold text-gray-900">{title}</h3>
      <p className={cn(siteUi.mutedXs, "mt-1 leading-relaxed")}>{description}</p>
    </>
  );

  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={className}>
        {content}
      </button>
    );
  }

  return (
    <Link to={to ?? "#"} className={className}>
      {content}
    </Link>
  );
}

export function SiteContentCard({
  children,
  className,
  padding = "p-5",
}: {
  children: ReactNode;
  className?: string;
  padding?: string;
}) {
  return (
    <Card
      className={cn(
        "overflow-hidden rounded-2xl border-white/40 bg-white/80 shadow-lg backdrop-blur-sm",
        className,
      )}
    >
      <CardContent className={cn(padding, "p-0")}>{children}</CardContent>
    </Card>
  );
}

export function SiteEmptyState({ message }: { message: string }) {
  return (
    <SiteGlassCard className="px-6 py-12 text-center">
      <p className={siteUi.muted}>{message}</p>
    </SiteGlassCard>
  );
}

export function SiteCtaBanner({
  title,
  description,
  action,
}: {
  title: string;
  description: string;
  action: ReactNode;
}) {
  return (
    <section
      className={cn(
        siteUi.gradientBg,
        "rounded-2xl p-8 text-center text-white shadow-xl md:p-10",
      )}
    >
      <h2 className="text-2xl font-bold">{title}</h2>
      <p className="mt-2 text-white/85">{description}</p>
      <div className="mt-6 flex justify-center">{action}</div>
    </section>
  );
}
